import React, { PureComponent } from "react";
import { LazyBrush } from "lazy-brush"; // default export
import { getCatenaryCurve, drawResult } from "catenary-curve";
import ResizeObserver from "resize-observer-polyfill";

import CoordinateSystem, { IDENTITY } from "./coordinateSystem";
import drawImage from "./drawImage";
import { DefaultState } from "./interactionStateMachine";
import makePassiveEventOption from "./makePassiveEventOption";

/** ----- Types ----- */

type Dim = number | string;

export interface CanvasDrawProps {
  onChange?: (api: CanvasDraw) => void;
  loadTimeOffset?: number;
  lazyRadius?: number;
  brushRadius?: number;
  brushColor?: string;
  catenaryColor?: string;
  gridColor?: string;
  backgroundColor?: string;
  hideGrid?: boolean;
  canvasWidth?: Dim;
  canvasHeight?: Dim;
  disabled?: boolean;
  imgSrc?: string;
  saveData?: string;
  immediateLoading?: boolean;
  hideInterface?: boolean;
  gridSizeX?: number;
  gridSizeY?: number;
  gridLineWidth?: number;
  hideGridX?: boolean;
  hideGridY?: boolean;
  enablePanAndZoom?: boolean;
  mouseZoomFactor?: number;
  zoomExtents?: { min: number; max: number };
  clampLinesToDocument?: boolean;

  className?: string;
  style?: React.CSSProperties;
}

type Point = { x: number; y: number };
type Line = { points: Point[]; brushColor: string; brushRadius: number };

type CtxMap = {
  grid?: CanvasRenderingContext2D;
  drawing?: CanvasRenderingContext2D;
  temp?: CanvasRenderingContext2D;
  interface?: CanvasRenderingContext2D;
};

type CanvasMap = {
  grid?: HTMLCanvasElement;
  drawing?: HTMLCanvasElement;
  temp?: HTMLCanvasElement;
  interface?: HTMLCanvasElement;
};

/** helper */
function midPointBtw(p1: Point, p2: Point): Point {
  return { x: p1.x + (p2.x - p1.x) / 2, y: p1.y + (p2.y - p1.y) / 2 };
}

const canvasStyle: React.CSSProperties = {
  display: "block",
  position: "absolute",
};
const canvasTypes = ["grid", "drawing", "temp", "interface"] as const;

export default class CanvasDraw extends PureComponent<CanvasDrawProps> {
  /** ----- Defaults ----- */
  static defaultProps: Required<
    Pick<
      CanvasDrawProps,
      | "onChange"
      | "loadTimeOffset"
      | "lazyRadius"
      | "brushRadius"
      | "brushColor"
      | "catenaryColor"
      | "gridColor"
      | "backgroundColor"
      | "hideGrid"
      | "canvasWidth"
      | "canvasHeight"
      | "disabled"
      | "imgSrc"
      | "saveData"
      | "immediateLoading"
      | "hideInterface"
      | "gridSizeX"
      | "gridSizeY"
      | "gridLineWidth"
      | "hideGridX"
      | "hideGridY"
      | "enablePanAndZoom"
      | "mouseZoomFactor"
      | "zoomExtents"
      | "clampLinesToDocument"
    >
  > = {
    onChange: () => {}, // instead of null
    loadTimeOffset: 5,
    lazyRadius: 12,
    brushRadius: 10,
    brushColor: "#444",
    catenaryColor: "#0a0302",
    gridColor: "rgba(150,150,150,0.17)",
    backgroundColor: "#FFF",
    hideGrid: false,
    canvasWidth: 400,
    canvasHeight: 400,
    disabled: false,
    imgSrc: "",
    saveData: "",
    immediateLoading: false,
    hideInterface: false,
    gridSizeX: 25,
    gridSizeY: 25,
    gridLineWidth: 0.5,
    hideGridX: false,
    hideGridY: false,
    enablePanAndZoom: false,
    mouseZoomFactor: 0.01,
    zoomExtents: { min: 0.33, max: 3 },
    clampLinesToDocument: false,
  };

  /** ----- Instance fields ----- */
  private canvas: CanvasMap = {};
  private ctx: CtxMap = {};
  private canvasContainer?: HTMLDivElement;

  private lazy!: LazyBrush;
  private chainLength = 0;
  private points: Point[] = [];
  private lines: Line[] = [];
  private erasedLines: Line[][] = [];

  private mouseHasMoved = true;
  private valuesChanged = true;
  private deferRedrawOnViewChange = false;

  private interactionSM = new DefaultState();
  private coordSystem: CoordinateSystem;

  private image?: HTMLImageElement;
  private canvasObserver!: ResizeObserver;

  constructor(props: CanvasDrawProps) {
    super(props);
    this.coordSystem = new CoordinateSystem({
      scaleExtents: props.zoomExtents,
      documentSize: {
        width: Number(props.canvasWidth),
        height: Number(props.canvasHeight),
      },
    });
    this.coordSystem.attachViewChangeListener(this.applyView.bind(this));
  }

  /** ----- Public API ----- */
  undo = () => {
    let lines: Line[] = [];
    if (this.lines.length) lines = this.lines.slice(0, -1);
    else if (this.erasedLines.length) lines = this.erasedLines.pop()!;
    this.clearExceptErasedLines();
    this.simulateDrawingLines({ lines, immediate: true });
    this.triggerOnChange();
  };

  eraseAll = () => {
    this.erasedLines.push([...this.lines]);
    this.clearExceptErasedLines();
    this.triggerOnChange();
  };

  clear = () => {
    this.erasedLines = [];
    this.clearExceptErasedLines();
    this.resetView();
  };

  resetView = () => this.coordSystem.resetView();
  setView = (view: any) => this.coordSystem.setView(view);

  getSaveData = () =>
    JSON.stringify({
      lines: this.lines,
      width: this.props.canvasWidth,
      height: this.props.canvasHeight,
    });

 getDataURL = (
  fileType: string = "png",
  includeBackgroundImage: boolean = false,
  backgroundColour?: string
): string => {
  const drawing = this.canvas.drawing!;
  const grid = this.canvas.grid!;
  const w = drawing.width;
  const h = drawing.height;

  // Compose into an offscreen canvas
  const off = document.createElement("canvas");
  off.width = w;
  off.height = h;
  const octx = off.getContext("2d")!;

  // 1) Optional solid background
  if (backgroundColour != null) {
    octx.fillStyle = backgroundColour;
    octx.fillRect(0, 0, w, h);
  }

  // 2) Background image (and grid if visible)
  // Your image is drawn onto the GRID context via drawImage/redrawImage.
  if (includeBackgroundImage && grid) {
    // make sure the grid layer has the latest image
    this.redrawImage();
    // copy the rasterized grid layer (contains the bg image)
    octx.drawImage(grid, 0, 0, w, h);
  }

  // 3) Ink/strokes layer on top
  octx.drawImage(drawing, 0, 0, w, h);

  // 4) Export merged PNG
  return off.toDataURL(`image/${fileType}`);
};


  loadSaveData = (
    saveData: string,
    immediate: boolean = this.props.immediateLoading ?? false
  ) => {
    if (typeof saveData !== "string")
      throw new Error("saveData needs to be of type string!");

    const { lines, width, height } = JSON.parse(saveData) as {
      lines: Line[];
      width: number;
      height: number;
    };
    if (!lines || typeof (lines as any).push !== "function")
      throw new Error("saveData.lines needs to be an array!");

    this.clear();

    if (
      width === Number(this.props.canvasWidth) &&
      height === Number(this.props.canvasHeight)
    ) {
      this.simulateDrawingLines({ lines, immediate });
    } else {
      const scaleX = Number(this.props.canvasWidth) / width;
      const scaleY = Number(this.props.canvasHeight) / height;
      const scaleAvg = (scaleX + scaleY) / 2;

      this.simulateDrawingLines({
        lines: lines.map((line) => ({
          ...line,
          points: line.points.map((p) => ({
            x: p.x * scaleX,
            y: p.y * scaleY,
          })),
          brushRadius: line.brushRadius * scaleAvg,
        })),
        immediate,
      });
    }
  };

  /** ----- Lifecycle ----- */

  componentDidMount() {
    this.lazy = new LazyBrush({
      radius: (this.props.lazyRadius ?? 12) * window.devicePixelRatio,
      enabled: true,
      initialPoint: { x: window.innerWidth / 2, y: window.innerHeight / 2 },
    });
    this.chainLength = (this.props.lazyRadius ?? 12) * window.devicePixelRatio;

    this.canvasObserver = new ResizeObserver((entries) =>
      this.handleCanvasResize(entries)
    );
    if (this.canvasContainer) this.canvasObserver.observe(this.canvasContainer);

    this.drawImage();
    this.loop();

    window.setTimeout(() => {
      const initX = window.innerWidth / 2;
      const initY = window.innerHeight / 2;
      this.lazy.update({ x: initX - this.chainLength / 4, y: initY }, {
        both: true,
      } as any);
      this.lazy.update({ x: initX + this.chainLength / 4, y: initY }, {
        both: false,
      } as any);
      this.mouseHasMoved = true;
      this.valuesChanged = true;
      this.clearExceptErasedLines();

      if (this.props.saveData) this.loadSaveData(this.props.saveData);
    }, 100);

    // non-passive wheel listener
    this.canvas.interface?.addEventListener(
      "wheel",
      this.handleWheel as any,
      makePassiveEventOption(true)
    );
  }

  componentDidUpdate(prevProps: CanvasDrawProps) {
    if (prevProps.lazyRadius !== this.props.lazyRadius) {
      this.chainLength =
        (this.props.lazyRadius ?? 12) * window.devicePixelRatio;
      this.lazy.setRadius(
        (this.props.lazyRadius ?? 12) * window.devicePixelRatio
      );
    }

    if (prevProps.saveData !== this.props.saveData && this.props.saveData) {
      this.loadSaveData(this.props.saveData);
    }

    if (JSON.stringify(prevProps) !== JSON.stringify(this.props))
      this.valuesChanged = true;

    (this.coordSystem as any).scaleExtents = this.props.zoomExtents;
    if (!this.props.enablePanAndZoom) this.coordSystem.resetView();

    if (prevProps.imgSrc !== this.props.imgSrc) this.drawImage();
  }

  componentWillUnmount = () => {
    if (this.canvasContainer)
      this.canvasObserver.unobserve(this.canvasContainer);
    this.canvas.interface?.removeEventListener(
      "wheel",
      this.handleWheel as any
    );
  };

  /** ----- Render ----- */
  render() {
    return (
      <div
        className={this.props.className}
        style={{
          display: "block",
          background: this.props.backgroundColor,
          touchAction: "none",
          width: this.props.canvasWidth,
          height: this.props.canvasHeight,
          ...this.props.style,
        }}
        ref={(container) => {
          this.canvasContainer = container ?? undefined;
        }}
      >
        {canvasTypes.map((name) => {
          const isInterface = name === "interface";
          return (
            <canvas
              key={name}
              ref={(canvas) => {
                if (canvas) {
                  this.canvas[name] = canvas;
                  this.ctx[name] = canvas.getContext("2d") ?? undefined;
                  if (isInterface) (this.coordSystem as any).canvas = canvas;
                }
              }}
              style={{ ...canvasStyle }}
              onMouseDown={
                isInterface ? (this.handleDrawStart as any) : undefined
              }
              onMouseMove={
                isInterface ? (this.handleDrawMove as any) : undefined
              }
              onMouseUp={isInterface ? (this.handleDrawEnd as any) : undefined}
              onMouseOut={isInterface ? (this.handleDrawEnd as any) : undefined}
              onTouchStart={
                isInterface ? (this.handleDrawStart as any) : undefined
              }
              onTouchMove={
                isInterface ? (this.handleDrawMove as any) : undefined
              }
              onTouchEnd={isInterface ? (this.handleDrawEnd as any) : undefined}
              onTouchCancel={
                isInterface ? (this.handleDrawEnd as any) : undefined
              }
            />
          );
        })}
      </div>
    );
  }

  /** ----- Events & view ----- */

  private handleWheel = (e: WheelEvent) => {
    this.interactionSM = (this.interactionSM as any).handleMouseWheel(e, this);
  };

  private handleDrawStart = (e: MouseEvent | TouchEvent) => {
    this.interactionSM = (this.interactionSM as any).handleDrawStart(e, this);
    this.mouseHasMoved = true;
  };

  private handleDrawMove = (e: MouseEvent | TouchEvent) => {
    this.interactionSM = (this.interactionSM as any).handleDrawMove(e, this);
    this.mouseHasMoved = true;
  };

  private handleDrawEnd = (e: MouseEvent | TouchEvent) => {
    this.interactionSM = (this.interactionSM as any).handleDrawEnd(e, this);
    this.mouseHasMoved = true;
  };

  private applyView = () => {
    if (!this.ctx.drawing) return;

    (
      canvasTypes
        .map((n) => this.ctx[n])
        .filter(Boolean) as CanvasRenderingContext2D[]
    ).forEach((ctx) => {
      this.clearWindow(ctx);
      const m = (this.coordSystem as any).transformMatrix;
      ctx.setTransform(m.a, m.b, m.c, m.d, m.e, m.f);
    });

    if (!this.deferRedrawOnViewChange) {
      this.drawGrid(this.ctx.grid!);
      this.redrawImage();
      this.loop({ once: true });

      const lines = this.lines;
      this.lines = [];
      this.simulateDrawingLines({ lines, immediate: true });
    }
  };

  private handleCanvasResize = (entries: ResizeObserverEntry[]) => {
    const saveData = this.getSaveData();
    this.deferRedrawOnViewChange = true;
    try {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        this.setCanvasSize(this.canvas.interface!, width, height);
        this.setCanvasSize(this.canvas.drawing!, width, height);
        this.setCanvasSize(this.canvas.temp!, width, height);
        this.setCanvasSize(this.canvas.grid!, width, height);

        (this.coordSystem as any).documentSize = { width, height };
        this.drawGrid(this.ctx.grid!);
        if (this.image && this.image.complete) {
          this.redrawImage();
        } else {
          this.drawImage();
        }
        this.loop({ once: true });
      }
      this.loadSaveData(saveData, true);
    } finally {
      this.deferRedrawOnViewChange = false;
    }
  };

  /** ----- Helpers ----- */

  private clampPointToDocument = (point: Point): Point => {
    if (this.props.clampLinesToDocument) {
      return {
        x: Math.max(Math.min(point.x, Number(this.props.canvasWidth)), 0),
        y: Math.max(Math.min(point.y, Number(this.props.canvasHeight)), 0),
      };
    }

    return point;
  };

  private redrawImage = () => {
    if (this.image && this.image.complete)
      drawImage({ ctx: this.ctx.grid!, img: this.image });
  };

  private simulateDrawingLines = ({
    lines,
    immediate,
  }: {
    lines: Line[];
    immediate: boolean;
  }) => {
    let curTime = 0;
    const timeoutGap = immediate ? 0 : this.props.loadTimeOffset ?? 5;

    lines.forEach((line) => {
      const { points, brushColor, brushRadius } = line;

      if (immediate) {
        this.drawPoints({ points, brushColor, brushRadius });
        this.points = points;
        this.saveLine({ brushColor, brushRadius });
        return;
      }

      for (let i = 1; i < points.length; i++) {
        curTime += timeoutGap;
        window.setTimeout(() => {
          this.drawPoints({
            points: points.slice(0, i + 1),
            brushColor,
            brushRadius,
          });
        }, curTime);
      }

      curTime += timeoutGap;
      window.setTimeout(() => {
        this.points = points;
        this.saveLine({ brushColor, brushRadius });
      }, curTime);
    });
  };

  private setCanvasSize = (
    canvas: HTMLCanvasElement,
    width: number,
    height: number
  ) => {
    canvas.width = width;
    canvas.height = height;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
  };

  private drawPoints = ({
    points,
    brushColor,
    brushRadius,
  }: {
    points: Point[];
    brushColor: string;
    brushRadius: number;
  }) => {
    const ctx = this.ctx.temp!;
    ctx.lineJoin = "round";
    ctx.lineCap = "round";
    ctx.strokeStyle = brushColor;

    this.clearWindow(ctx);
    ctx.lineWidth = brushRadius * 2;

    let p1 = points[0];
    let p2 = points[1];

    ctx.moveTo(p2.x, p2.y);
    ctx.beginPath();

    for (let i = 1, len = points.length; i < len; i++) {
      const mid = midPointBtw(p1, p2);
      ctx.quadraticCurveTo(p1.x, p1.y, mid.x, mid.y);
      p1 = points[i];
      p2 = points[i + 1];
    }
    ctx.lineTo(p1.x, p1.y);
    ctx.stroke();
  };

  private saveLine = ({
    brushColor,
    brushRadius,
  }: { brushColor?: string; brushRadius?: number } = {}) => {
    if (this.points.length < 2) return;

    this.lines.push({
      points: [...this.points],
      brushColor: brushColor ?? this.props.brushColor!,
      brushRadius: brushRadius ?? this.props.brushRadius!,
    });

    this.points.length = 0;

    this.inClientSpace([this.ctx.drawing!, this.ctx.temp!], () => {
      this.ctx.drawing!.drawImage(
        this.canvas.temp!,
        0,
        0,
        this.canvas.drawing!.width,
        this.canvas.drawing!.height
      );
    });

    this.clearWindow(this.ctx.temp!);
    this.triggerOnChange();
  };

  private triggerOnChange = () => {
    this.props.onChange?.(this);
  };

  private clearWindow = (ctx: CanvasRenderingContext2D) => {
    this.inClientSpace([ctx], () =>
      ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height)
    );
  };

  private clearExceptErasedLines = () => {
    this.lines = [];
    this.valuesChanged = true;
    this.clearWindow(this.ctx.drawing!);
    this.clearWindow(this.ctx.temp!);
  };

  private loop = ({ once = false }: { once?: boolean } = {}) => {
    if (this.mouseHasMoved || this.valuesChanged) {
      const pointer = (this.lazy as any).getPointerCoordinates() as Point;
      const brush = (this.lazy as any).getBrushCoordinates() as Point;

      this.drawInterface(this.ctx.interface!, pointer, brush);
      this.mouseHasMoved = false;
      this.valuesChanged = false;
    }

    if (!once) window.requestAnimationFrame(() => this.loop());
  };

  private inClientSpace = (
    ctxs: CanvasRenderingContext2D[],
    action: () => void
  ) => {
    ctxs.forEach((ctx) => {
      ctx.save();
      ctx.setTransform(
        IDENTITY.a,
        IDENTITY.b,
        IDENTITY.c,
        IDENTITY.d,
        IDENTITY.e,
        IDENTITY.f
      );
    });
    try {
      action();
    } finally {
      ctxs.forEach((ctx) => ctx.restore());
    }
  };

  /** ----- Rendering helpers ----- */

  private drawImage = () => {
    if (!this.props.imgSrc) {
      this.image = undefined;
      return;
    }

    const resolvedSrc =
      typeof window !== "undefined"
        ? new URL(this.props.imgSrc, window.location.href).href
        : this.props.imgSrc;

    if (this.image && this.image.src === resolvedSrc) {
      if (this.image.complete) this.redrawImage();
      return;
    }

    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      this.image = img;
      this.redrawImage();
    };
    img.src = this.props.imgSrc;
    this.image = img;
  };

  private drawGrid = (ctx: CanvasRenderingContext2D) => {
    if (this.props.hideGrid) return;
    this.clearWindow(ctx);

    const gridSize = 25;
    const { viewMin, viewMax } = (this.coordSystem as any).canvasBounds;
    const minx = Math.floor(viewMin.x / gridSize - 1) * gridSize;
    const miny = Math.floor(viewMin.y / gridSize - 1) * gridSize;
    const maxx = viewMax.x + gridSize;
    const maxy = viewMax.y + gridSize;

    ctx.beginPath();
    ctx.setLineDash([5, 1]);
    ctx.setLineDash([]);
    ctx.strokeStyle = this.props.gridColor!;
    ctx.lineWidth = this.props.gridLineWidth!;

    if (!this.props.hideGridX) {
      let countX = minx;
      const gridSizeX = this.props.gridSizeX!;
      while (countX < maxx) {
        countX += gridSizeX;
        ctx.moveTo(countX, miny);
        ctx.lineTo(countX, maxy);
      }
      ctx.stroke();
    }

    if (!this.props.hideGridY) {
      let countY = miny;
      const gridSizeY = this.props.gridSizeY!;
      while (countY < maxy) {
        countY += gridSizeY;
        ctx.moveTo(minx, countY);
        ctx.lineTo(maxx, countY);
      }
      ctx.stroke();
    }
  };

  private drawInterface = (
    ctx: CanvasRenderingContext2D,
    pointer: Point,
    brush: Point
  ) => {
    if (this.props.hideInterface) return;

    this.clearWindow(ctx);

    // brush preview
    ctx.beginPath();
    ctx.fillStyle = this.props.brushColor!;
    ctx.arc(brush.x, brush.y, this.props.brushRadius!, 0, Math.PI * 2, true);
    ctx.fill();

    // mouse point
    ctx.beginPath();
    ctx.fillStyle = this.props.catenaryColor!;
    ctx.arc(pointer.x, pointer.y, 4, 0, Math.PI * 2, true);
    ctx.fill();

    // catenary line
    if ((this.lazy as any).isEnabled()) {
      ctx.beginPath();
      ctx.lineWidth = 2;
      ctx.lineCap = "round";
      ctx.setLineDash([2, 4]);
      ctx.strokeStyle = this.props.catenaryColor!;

      const result = getCatenaryCurve(
        { x: brush.x, y: brush.y },
        { x: pointer.x, y: pointer.y },
        this.chainLength
      );
      drawResult(result, ctx); // draws line/quadratic segments onto ctx

      ctx.stroke();
    }

    // brush center point
    ctx.beginPath();
    ctx.fillStyle = this.props.catenaryColor!;
    ctx.arc(brush.x, brush.y, 2, 0, Math.PI * 2, true);
    ctx.fill();
  };
}
